/**
 * Cookiebot Consent Manager integration wired to Google Consent Mode v2.
 *
 * Loads the Cookiebot banner (`uc.js`) for the tenant's Domain Group ID, declares
 * the Google Consent Mode default state *before* GTM initializes, and pushes
 * `gtag('consent', 'update', ...)` as the visitor accepts or declines categories
 * (via Cookiebot's `CookiebotOnAccept` / `CookiebotOnDecline` events).
 *
 * Gated by the `COOKIEBOT_CONSENT_MANAGER_ENABLED` whitelabel feature and the
 * tenant's `env.cookiebotId`, so it has no effect on tenants that have not opted in.
 *
 * Runs in manual blocking mode: cookie-blocking is delegated to GTM's consent
 * gating (Consent Mode) rather than Cookiebot's auto-blocking, because the script
 * is injected after the page has loaded.
 *
 * @see https://www.cookiebot.com/en/google-consent-mode/
 * @module services/cookiebotConsentManager
 */

const CDN_URL = 'https://consent.cookiebot.com/uc.js';
const SCRIPT_ELEMENT_ID = 'Cookiebot';

/**
 * Reads the persisted Cookiebot consent for a category from the `CookieConsent`
 * cookie and maps it to a Google Consent Mode signal. Lets returning visitors
 * reuse their stored choice for the synchronous default (before Cookiebot loads).
 * @param {string} category - The Cookiebot category (`'statistics'` | `'marketing'`).
 * @returns {'granted'|'denied'} The Consent Mode value for a returning visitor.
 */
const storedConsentSignal = (category) => {
  try {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith('CookieConsent='));
    if (!cookie) return 'denied';
    const value = decodeURIComponent(cookie.split('=').slice(1).join('='));
    return new RegExp(`${category}:true`).test(value) ? 'granted' : 'denied';
  } catch (error) {
    return 'denied';
  }
};

/**
 * Ensures the shared `dataLayer` and a global `gtag` helper exist. Pushes to the
 * same `window.dataLayer` that react-gtm-module uses by default.
 * @returns {(...args: unknown[]) => void} The global `gtag` function.
 */
const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
};

/**
 * Declares the Google Consent Mode default state. Must run before GTM loads so
 * consent-aware tags hold until the visitor decides. First-time visitors start
 * denied for analytics/marketing; returning visitors reuse their stored choice.
 * @returns {void}
 */
const pushConsentDefault = () => {
  const gtag = ensureGtag();
  const marketing = storedConsentSignal('marketing');
  gtag('consent', 'default', {
    analytics_storage: storedConsentSignal('statistics'),
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
};

/**
 * Pushes a `gtag('consent', 'update', ...)` reflecting Cookiebot's current
 * category choices. Bound to Cookiebot's accept/decline events so Consent Mode
 * stays in sync as the visitor changes preferences.
 * @returns {void}
 */
const pushConsentUpdate = () => {
  const consent = window.Cookiebot?.consent;
  if (!consent) return;
  const gtag = ensureGtag();
  const analytics = consent.statistics ? 'granted' : 'denied';
  const marketing = consent.marketing ? 'granted' : 'denied';
  gtag('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
};

/**
 * Injects the Cookiebot script for the given Domain Group ID and wires its
 * accept/decline events to Consent Mode updates. No-op if already injected.
 * @param {string} cookiebotId - The tenant's Cookiebot Domain Group ID (`data-cbid`).
 * @param {string} [language] - Active i18n language; mapped to `data-culture`.
 * @returns {void}
 */
const loadCookiebot = (cookiebotId, language) => {
  if (document.getElementById(SCRIPT_ELEMENT_ID)) return;

  window.addEventListener('CookiebotOnAccept', pushConsentUpdate);
  window.addEventListener('CookiebotOnDecline', pushConsentUpdate);

  const script = document.createElement('script');
  script.id = SCRIPT_ELEMENT_ID;
  script.src = CDN_URL;
  script.async = true;
  script.setAttribute('data-cbid', cookiebotId);
  script.setAttribute('data-blockingmode', 'manual');
  if (typeof language === 'string' && language) {
    script.setAttribute('data-culture', language.slice(0, 2).toUpperCase());
  }
  document.head.appendChild(script);
};

/**
 * Boots the Cookiebot Consent Manager with Google Consent Mode v2.
 *
 * Call this *before* GTM initializes so the consent default is queued first.
 * Idempotent: repeated calls are ignored. No-op without an `appId`.
 * @param {object} [options] - Initialization options.
 * @param {string} [options.appId] - Tenant Cookiebot Domain Group ID (`data-cbid`).
 * @param {string} [options.language] - Active i18n language for banner culture.
 * @returns {void}
 */
const initCookiebotConsentManager = ({ appId, language } = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!appId) return;
  if (window.cookiebotConsentManagerBooted) return;
  window.cookiebotConsentManagerBooted = true;

  pushConsentDefault();
  loadCookiebot(appId, language);
};

export default initCookiebotConsentManager;
